import { days, states } from "@ac-expo/api-types";
import { Time } from "@internationalized/date";
import { Day, EventTimetableState } from "@prisma/client";
import { DefaultValues } from "react-hook-form";
import { z } from "zod";

const INVALID_HOUR = "Heure invalide";

const zTime = z.instanceof(Time, { message: INVALID_HOUR });

const upsertEventTimetableSchema = z
  .object({
    id: z.string().optional(),
    day: z.enum(days as [Day, ...Day[]]),
    state: z.enum(states as [EventTimetableState, ...EventTimetableState[]]),
    openingTime: zTime.nullish(),
    closingTime: zTime.nullish(),
    breakStartTime: zTime.nullish(),
    breakEndTime: zTime.nullish(),
  })
  .refine(({ state, openingTime }) => state === "closed" || openingTime, {
    message: INVALID_HOUR,
    path: ["openingTime"],
  })
  .refine(
    ({ state, closingTime, openingTime, breakEndTime }) => {
      if (state === "closed") return true;
      if (!closingTime) return false;
      if (state === "continuouslyOpened")
        return !openingTime || closingTime.compare(openingTime) > 0;
      return !breakEndTime || closingTime.compare(breakEndTime) > 0;
    },
    {
      message: INVALID_HOUR,
      path: ["closingTime"],
    },
  )
  .refine(
    ({ state, breakStartTime, openingTime }) => {
      if (state !== "opened") return true;
      if (!breakStartTime) return false;
      return !openingTime || breakStartTime.compare(openingTime) > 0;
    },
    {
      message: INVALID_HOUR,
      path: ["breakStartTime"],
    },
  )
  .refine(
    ({ state, breakEndTime, breakStartTime }) => {
      if (state !== "opened") return true;
      if (!breakEndTime) return false;
      return !breakStartTime || breakEndTime.compare(breakStartTime) > 0;
    },
    {
      message: INVALID_HOUR,
      path: ["breakEndTime"],
    },
  );

export type EventTimetable = z.infer<typeof upsertEventTimetableSchema>;

export const upsertEventExceptionalClosingDateSchema = z.object({
  id: z.string().optional(),
  date: z.date().refine((date) => !isNaN(date.getTime()), {
    message: "Date invalide",
  }),
});

export type EventExceptionalClosingDateForm = z.infer<
  typeof upsertEventExceptionalClosingDateSchema
>;

export const defaultEventExceptionalDateValues: DefaultValues<EventExceptionalClosingDateForm> = {
  date: undefined,
};

export const upsertEventDateRangeSchema = z
  .object({
    id: z.string().optional(),
    dates: z
      .object({
        from: z.date().refine((date) => !isNaN(date.getTime()), {
          message: "Date de début invalide",
        }),
        to: z.date().refine((date) => !isNaN(date.getTime()), {
          message: "Date de fin invalide",
        }),
      })
      .nullish(),
    timetables: z.array(upsertEventTimetableSchema).refine(
      (data) => {
        const days = data.map((timetable) => timetable.day);
        return new Set(days).size === days.length;
      },
      {
        message: "Tous les jours doivent être renseignés",
      },
    ),
  })
  .refine(({ dates }) => (dates ? dates?.from && dates.to : true), {
    message: "Veuillez renseigner les dates d'ouverture",
  });

export type EventDateRangeForm = z.infer<typeof upsertEventDateRangeSchema>;

export const defaultEventDateRangeValues: EventDateRangeForm = {
  timetables: days.map((day) => ({
    day,
    state: "continuouslyOpened",
    type: day === "sunday" ? "close" : "open",
  })),
};

export const defaultEventDateValues = {
  schedules: [],
  exceptionalClosingDates: [],
  isContinuous: false,
  id: "",
  eventId: "",
};
